import { formatCurrency } from "@lib/formatters";
import {
  getActiveFunders,
  getActiveProposals,
  getEvaluatorMvfProfiles,
  getProposalOrPlaceholder,
  userIsMeanRecommender,
} from "@engine/round/accessors";
import {
  type Node,
  type Recommender,
  type RoundAggregate,
} from "@engine/round/aggregate";
import { type MVFPL, weightedUtility } from "@lib/specGrantSettlementAlgo";
import { type MVFProfile } from "@lib/tranche";
import { interleave } from "@lib/utils/utils";
import type Matrix from "algorithm/matrix";
import NamedMatrix from "algorithm/NamedMatrix";
import type Vector from "algorithm/vector";
import { runFunderLayer } from "./funderLayer";
import sum from "lodash/sum";
import { buildFundingPhases, type FundingPhases } from "./fundingPhases";
import { getBudgetPortionsFromFunderAccounts } from "./recommenderBudgetPortions";
import { runRecommenderLayer } from "./recommenderLayer";
import {
  getFunderLayerInput,
  getRecommenderLayerInput,
  type FunderLayerInput,
  type RecommenderLayerInput,
} from "./recommenderPreferences";

export {
  buildMatchingPreferenceMatrix,
  getMatchingProfileCsr,
  getRecommenderLayerInput,
  getRecommenderLayerInputUncached,
  getStartingAdvancements,
  makeMatchingTranchesFromStart,
} from "./recommenderPreferences";

const BUDGET_EPSILON = 1000;

export type MultilayerInitialGuarantees = [
  NamedMatrix<"funder", "recommender+hold">,
  NamedMatrix<"recommender+hold", "proposal+hold">,
];

/**
 * The input to a two-layer S-Process
 */
export type SProcessInput = {
  parentNodes: Node[];
  layerNodes: [Node[], Node[]];
  parentBudgets: Vector<"funder">;
  layerPreferenceLists: [
    Matrix<"tranche", "recommender+hold">[],
    Matrix<"tranche", "proposal+hold">[],
  ];
  holdProposalId: string;
  initialGuarantees?: MultilayerInitialGuarantees;
};

export type SProcessOutput = {
  parentNodes: Node[];
  layerNodes: [Node[], Node[]];
  layerTargetTotals: [Vector<"recommender+hold">, Vector<"proposal+hold">]; // total amount allocated to each evaluable (in each layer)
  layerAccounts: [
    NamedMatrix<"funder", "recommender+hold">,
    NamedMatrix<"recommender+hold", "proposal+hold">,
  ];
  layerGuarantees: [
    NamedMatrix<"funder", "recommender+hold">,
    NamedMatrix<"recommender+hold", "proposal+hold">,
  ];
  layerBudgets: [Vector<"funder">, Vector<"recommender">];
  holdProposalId: string;
};

export type SProcessOutputWithMatchingAdjustment = {
  standardFunderAccounts: NamedMatrix<"funder", "recommender+hold">;
  standardAccounts: NamedMatrix<"recommender", "proposal+hold">;
  standardGuarantees?: NamedMatrix<"recommender", "proposal+hold">;
  outputWithMatching: SProcessOutput;
  warnings: string[];
  proposalIndicesMap: { [proposalId: string]: number };
  evaluatorMvfs: { [evaluatorId: string]: MVFProfile };
  matchingEvaluatorMvfs: { [evaluatorId: string]: MVFProfile };
  fundingPhases: FundingPhases;
};

function buildSProcessOutput({
  funderLayerInput,
  recommenderLayerInput,
  funderAccounts,
  funderGuarantees,
  recommenderAccounts,
  recommenderGuarantees,
  funderBudgets,
  recommenderBudgets,
}: {
  funderLayerInput: FunderLayerInput;
  recommenderLayerInput: RecommenderLayerInput;
  funderAccounts: Matrix<"evaluator", "evaluable+hold">;
  funderGuarantees: Matrix<"evaluator", "evaluable+hold">;
  recommenderAccounts: Matrix<"evaluator", "evaluable+hold">;
  recommenderGuarantees: Matrix<"evaluator", "evaluable+hold">;
  funderBudgets: Vector<"funder">;
  recommenderBudgets: Vector<"recommender">;
}): SProcessOutput {
  const parentNodes = funderLayerInput.funders;
  const recommenderNodes: Node[] = [
    recommenderLayerInput.holdProposal,
    ...recommenderLayerInput.orderedRecommenders,
  ];
  const proposalNodes: Node[] = recommenderLayerInput.orderedProposals;
  const holdProposalId = recommenderLayerInput.holdProposal.id;

  const funderIds = funderLayerInput.funders.map((funder) => funder.id);
  const recommenderIdsWithHold = recommenderNodes.map((node) => node.id);
  const recommenderIdsWithoutHold =
    recommenderLayerInput.orderedRecommenders.map(
      (recommender) => recommender.id,
    );
  const proposalIds = recommenderLayerInput.orderedProposals.map(
    (proposal) => proposal.id,
  );

  return {
    parentNodes,
    layerNodes: [recommenderNodes, proposalNodes],
    layerTargetTotals: [
      funderAccounts.sum(0).rename("evaluable+hold -> recommender+hold"),
      recommenderAccounts.sum(0).rename("evaluable+hold -> proposal+hold"),
    ],
    layerAccounts: [
      NamedMatrix.fromMatrix(
        funderAccounts.rename(
          "evaluator -> funder",
          "evaluable+hold -> recommender+hold",
        ),
        funderIds,
        recommenderIdsWithHold,
      ),
      NamedMatrix.fromMatrix(
        recommenderAccounts.rename(
          "evaluator -> recommender+hold",
          "evaluable+hold -> proposal+hold",
        ),
        recommenderIdsWithoutHold,
        proposalIds,
      ),
    ],
    layerGuarantees: [
      NamedMatrix.fromMatrix(
        funderGuarantees.rename(
          "evaluator -> funder",
          "evaluable+hold -> recommender+hold",
        ),
        funderIds,
        recommenderIdsWithHold,
      ),
      NamedMatrix.fromMatrix(
        recommenderGuarantees.rename(
          "evaluator -> recommender+hold",
          "evaluable+hold -> proposal+hold",
        ),
        recommenderIdsWithoutHold,
        proposalIds,
      ),
    ],
    layerBudgets: [funderBudgets, recommenderBudgets],
    holdProposalId,
  };
}

/**
 * Give applications credit for the value of matching funds we expect them to receive.
 * 1. First run the algorithm to distribute all standard dollars using standard MVFs
 * 2. Use the result of that run as a starting point for a subsequent run to distribute matching dollars.
 *    This is operationalized by inserting the output from the standard algorithm as the first tranche
 *    in all matching tranche lists.
 */
export function runSProcessOnRoundWithMatchingAdjustment(
  round: RoundAggregate,
  excludedProposals: { [proposalId: string]: number } = {},
): SProcessOutputWithMatchingAdjustment & {
  phase1RecommenderLayerInput: RecommenderLayerInput;
} {
  const phase1RecommenderLayerInput = getRecommenderLayerInput(
    round,
    excludedProposals,
  );
  const funderLayerInput = getFunderLayerInput(
    round,
    phase1RecommenderLayerInput.recommenderIndicesMap,
  );

  const {
    accounts: funderAccounts,
    guarantees: funderGuarantees,
    standardAccounts: standardFunderAccountsMatrix,
    remainingBudget: funderRemainingBudget,
  } = runFunderLayer(round, funderLayerInput);

  // The real-recommender budget vector is still what buildSProcessOutput /
  // warnings report as layerBudgets[1]. layerAccounts[0] (funder -> recommender)
  // is likewise derived from funderAccounts unchanged.
  const [, phase2RecommenderBudgets] = funderAccounts
    .sum(0)
    .shift<"recommender">();

  const budgetPortionSet = getBudgetPortionsFromFunderAccounts(
    funderAccounts.rename(
      "evaluator -> funder",
      "evaluable+hold -> recommender+hold",
    ),
    funderLayerInput.funders,
    phase1RecommenderLayerInput,
    standardFunderAccountsMatrix.rename(
      "evaluator -> funder",
      "evaluable+hold -> recommender+hold",
    ),
  );
  const recommenderLayerResult = runRecommenderLayer({
    round,
    phase1RecommenderLayerInput,
    budgetPortionSet,
    excludedProposalIds: excludedProposals,
    debugLabelPrefix: "runSProcessOnRoundWithMatchingAdjustment",
    enforceFundingEligibility: false,
  });
  const {
    recommenderAccountsWithoutMatching,
    recommenderGuaranteesWithoutMatching,
    recommenderAccounts,
    recommenderGuarantees,
    remainingBudget: recommenderRemainingBudget,
  } = recommenderLayerResult;

  const outputWithMatching = buildSProcessOutput({
    funderLayerInput,
    recommenderLayerInput: phase1RecommenderLayerInput,
    funderAccounts,
    funderGuarantees,
    recommenderAccounts,
    recommenderGuarantees,
    funderBudgets: funderLayerInput.funderBudgets,
    recommenderBudgets: phase2RecommenderBudgets,
  });

  const evaluatorMvfs: { [evaluatorId: string]: MVFProfile } = {};
  const matchingEvaluatorMvfs: { [evaluatorId: string]: MVFProfile } = {};

  for (const recommender of phase1RecommenderLayerInput.orderedRecommenders) {
    const mvfProfiles = getEvaluatorMvfProfiles(round, recommender.id);
    evaluatorMvfs[recommender.id] = mvfProfiles.standard;
    matchingEvaluatorMvfs[recommender.id] = mvfProfiles.matching;
  }

  if (Object.keys(excludedProposals).length > 0) {
    console.log("Proposals not meeting minimum requests:");
    console.table(
      Object.entries(excludedProposals).map(([proposalId, amount]) => {
        const proposal = getProposalOrPlaceholder(round, proposalId);
        return {
          shortname: proposal.shortname,
          "Amount initially allocated": amount,
          "Minimum request": proposal.typedFields.minGrantAmount,
        };
      }),
    );
  }

  const warnings: string[] = [];

  if (funderRemainingBudget > BUDGET_EPSILON) {
    warnings.push(
      `${formatCurrency(funderRemainingBudget)} funds from the round budget haven’t been distributed to recommenders.`,
    );

    funderLayerInput.funderBudgets.forEach((budget, funderIndex) => {
      const allocatedBudget = funderAccounts.getRow(funderIndex).sum();

      if (allocatedBudget + BUDGET_EPSILON < budget) {
        const funder = funderLayerInput.funders[funderIndex];

        if (funder == null) {
          return;
        }

        warnings.push(
          `Funder ${funder.shortname} has a budget of ${formatCurrency(budget)} but only allocated ${formatCurrency(allocatedBudget)}.`,
        );
      }
    });
  }

  if (recommenderRemainingBudget > 0) {
    warnings.push(
      `${formatCurrency(recommenderRemainingBudget)} funds from recommenders haven’t been distributed to proposals.`,
    );

    phase2RecommenderBudgets.forEach((budget, recommenderIndex) => {
      // Each row is one real recommender pool, including grants returned to hold.
      const allocatedBudget = recommenderAccounts
        .getRow(recommenderIndex)
        .sum();

      if (allocatedBudget + BUDGET_EPSILON < budget) {
        const recommender =
          phase1RecommenderLayerInput.orderedRecommenders[recommenderIndex];

        if (recommender == null) {
          return;
        }

        warnings.push(
          `Recommender ${recommender.shortname} has a budget of ${formatCurrency(budget)} but only allocated ${formatCurrency(allocatedBudget)}.`,
        );
      }
    });
  }

  const standardFunderAccounts = NamedMatrix.fromMatrix(
    standardFunderAccountsMatrix.rename(
      "evaluator -> funder",
      "evaluable+hold -> recommender+hold",
    ),
    outputWithMatching.layerAccounts[0].rowNames,
    outputWithMatching.layerAccounts[0].colNames,
  );
  const standardAccounts = NamedMatrix.fromMatrix(
    recommenderAccountsWithoutMatching.rename(
      "evaluator -> recommender",
      "evaluable+hold -> proposal+hold",
    ),
    phase1RecommenderLayerInput.orderedRecommenders.map((r) => r.id),
    phase1RecommenderLayerInput.orderedProposals.map((p) => p.id),
  );
  const fundingPhases = buildFundingPhases(
    round,
    outputWithMatching,
    standardAccounts,
    standardFunderAccounts,
  );

  // Standard-phase matrices retain the same real-recommender rows as the full allocation.
  return {
    standardFunderAccounts,
    standardAccounts,
    standardGuarantees: NamedMatrix.fromMatrix(
      recommenderGuaranteesWithoutMatching.rename(
        "evaluator -> recommender",
        "evaluable+hold -> proposal+hold",
      ),
      phase1RecommenderLayerInput.orderedRecommenders.map((r) => r.id),
      phase1RecommenderLayerInput.orderedProposals.map((p) => p.id),
    ),
    outputWithMatching,
    warnings,
    proposalIndicesMap: phase1RecommenderLayerInput.proposalIndicesMap,
    evaluatorMvfs,
    matchingEvaluatorMvfs,
    phase1RecommenderLayerInput,
    fundingPhases,
  };
}

/**
 * Per-recommender budget weights for ranking one under-minimum proposal in
 * {@link runMinExclusion}. A funder that excludes the proposal can never route
 * money to it, so that funder's share of each recommender's funder-layer
 * inflow must not count toward the proposal's utility — otherwise a heavily
 * excluded proposal looks better-backed than it effectively is and a
 * different under-minimum proposal gets pruned in its place. Returns, per
 * recommender (in `recommenderIds` order), the fraction of its funder-layer
 * inflow coming from funders that do NOT exclude the proposal (1 when the
 * recommender has no inflow, so zero-inflow rows keep their existing
 * behavior).
 */
export function minExclusionBudgetWeights(
  funderAccounts: NamedMatrix<"funder", "recommender+hold">,
  funderExcludedProposalIds: ReadonlyArray<ReadonlySet<string>>,
  recommenderIds: string[],
  proposalId: string,
): number[] {
  return recommenderIds.map((recommenderId) => {
    const inflows = funderAccounts.getColName(recommenderId).toArray();
    const total = sum(inflows);

    if (total <= 0) {
      return 1;
    }

    const available = sum(
      inflows.filter(
        (_, funderIndex) =>
          !funderExcludedProposalIds[funderIndex]?.has(proposalId),
      ),
    );
    return available / total;
  });
}

export function* minimumExclusionSteps<
  T extends SProcessOutputWithMatchingAdjustment,
>(
  round: RoundAggregate,
  nonExclusionRun: T,
): Generator<
  Record<string, number>,
  T & { excludedProposals: Record<string, number> },
  T
> {
  let currentRun = nonExclusionRun;
  const excludedProposals: { [proposalId: string]: number } = {};

  while (true) {
    const {
      outputWithMatching: sProcessOutputWithMatchingAdjustment,
      evaluatorMvfs,
      matchingEvaluatorMvfs,
    } = currentRun;

    // Filter out proposals that don't meet their minimum request.
    const candidateOrgsByUtility: [number, string][] = [];
    const proposals = getActiveProposals(round);
    const budgets =
      sProcessOutputWithMatchingAdjustment.layerBudgets[1].toArray();

    // Funder exclusions per funder-layer row, for discounting each
    // recommender's budget weight on proposals its funders exclude. Aligned
    // with layerAccounts[0]'s funder rows; recomputed per re-run because the
    // funder-layer allocations shift as proposals get pruned.
    const funderAccounts =
      sProcessOutputWithMatchingAdjustment.layerAccounts[0];
    const fundersById = new Map(
      getActiveFunders(round).map((funder) => [funder.id, funder]),
    );
    const funderExcludedProposalIds = funderAccounts.rowNames.map(
      (funderId) =>
        new Set(fundersById.get(funderId)?.excludedProposalIds ?? []),
    );
    const roundHasExclusions = funderExcludedProposalIds.some(
      (excluded) => excluded.size > 0,
    );
    const recommenderIds = Object.keys(evaluatorMvfs);

    for (const proposal of proposals) {
      if (proposal.id in excludedProposals) {
        continue;
      }

      const amt = sProcessOutputWithMatchingAdjustment.layerAccounts[1]
        .getColName(proposal.id)
        .sum();
      const minRequest = proposal.typedFields.minGrantAmount ?? 0;

      if (amt < minRequest) {
        const filteredBudgets: number[] = [];
        const mvfs: MVFPL[] = [];

        // With no exclusions every weight is 1 and the ranking is byte-for-byte
        // the pre-feature one.
        const budgetWeights = roundHasExclusions
          ? minExclusionBudgetWeights(
              funderAccounts,
              funderExcludedProposalIds,
              recommenderIds,
              proposal.id,
            )
          : null;

        const interleavedMvfs = interleave(
          Object.values(evaluatorMvfs),
          Object.values(matchingEvaluatorMvfs),
        );

        for (let i = 0; i < interleavedMvfs.length; i++) {
          const mvfProfile = interleavedMvfs[i];

          if (mvfProfile == null) {
            continue;
          }

          const mvf = mvfProfile[proposal.id];

          if (mvf == null) {
            continue;
          }

          const rawBudget = budgets[Math.floor(i / 2)];

          if (rawBudget == null) {
            continue;
          }

          const budget =
            budgetWeights == null
              ? rawBudget
              : rawBudget * (budgetWeights[Math.floor(i / 2)] ?? 1);
          let bdv = 0;
          filteredBudgets.push(budget);
          mvfs.push(
            mvf.map((point) => {
              const tdv = point.value + bdv;
              const result = {
                tdv,
                bdv,
                size: point.dollar,
              };
              bdv += point.value;
              return result;
            }),
          );
        }

        const utility = weightedUtility(mvfs, filteredBudgets, amt);
        candidateOrgsByUtility.push([utility, proposal.id]);
      }
    }

    if (candidateOrgsByUtility.length === 0) {
      return {
        ...currentRun,
        excludedProposals,
      };
    }

    candidateOrgsByUtility.sort((a, b) => a[0] - b[0]);

    // Exclude one candidate, then recompute before ranking again. A zero
    // reconciled grant can still have a positive ideal allocation, so removing
    // it may free funding in the next ideal solve. Even a naturally zero grant
    // must be removed from the output columns before exports append exclusions.
    const proposalId = candidateOrgsByUtility[0]![1];
    const allocation = sProcessOutputWithMatchingAdjustment.layerAccounts[1]
      .getColName(proposalId)
      .sum();
    excludedProposals[proposalId] = allocation;

    if (allocation > 0) {
      const proposal = getProposalOrPlaceholder(round, proposalId);
      console.log(
        `excluding ${proposal.shortname} even though its allocation (${allocation}) is positive (minimum request: ${proposal.typedFields.minGrantAmount})`,
      );
    }

    currentRun = yield { ...excludedProposals };
  }
}

export function runMinExclusion(
  round: RoundAggregate,
  initial: SProcessOutputWithMatchingAdjustment,
) {
  const steps = minimumExclusionSteps(round, initial);
  let step = steps.next();

  while (!step.done) {
    step = steps.next(
      runSProcessOnRoundWithMatchingAdjustment(round, step.value),
    );
  }

  return step.value;
}

export function pythonCompatibleShortname(recommender: Recommender): string {
  return userIsMeanRecommender(recommender.memberId)
    ? "Mean"
    : recommender.shortname;
}
